// 案件モーダル＝3区画（§7-4）。
//  1) 事実欄（閲覧中心）
//  2) 経営側の評価・分類欄（経営側のみ編集。担当者には伏せる。本社確認(C類)のみ §9-2 で担当者にも開示）
//  3) 2系統コメントログ（会議メモ＝経営側のみ／担当者への連絡＝担当者にも可視）
// ＋ ロール別アクション（部門責任者: 承認/差し戻し、起案者: 編集/再提出）。
import { useState, type ReactNode } from "react";
import { Modal } from "../../components/Modal";
import { Button, Field, TextArea } from "../../components/ui";
import { StatusBadge } from "./StatusBadge";
import { ClassificationBadge, HqStatusBadge } from "./badges";
import { EvaluationPanel } from "./EvaluationPanel";
import { CommentSection } from "./CommentSection";
import { AttachmentSection } from "./AttachmentSection";
import {
  ContractForm,
  contractToFormValues,
  type FormAction,
} from "./ContractForm";
import {
  AMOUNT_TYPES,
  CONTINUITIES,
  CONTRACT_TYPES,
  SPECIAL_CLAUSES,
  labelOf,
  labelsOf,
} from "../../masters";
import { formatAmount, formatDate } from "../../lib/format";
import { api, ApiError } from "../../lib/api";
import { t } from "../../i18n/zh-TW";
import type { Contract, ContractFormValues, User } from "../../lib/types";

function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="grid grid-cols-3 gap-2 py-1.5">
      <dt className="col-span-1 text-sm text-slate-500">{label}</dt>
      <dd className="col-span-2 text-sm text-slate-800">{value}</dd>
    </div>
  );
}

export function ContractDetailModal({
  contract,
  user,
  onClose,
  onChanged,
}: {
  contract: Contract;
  user: User;
  onClose: () => void;
  onChanged: (updated: Contract) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");

  const isManagement = user.role === "secretariat" || user.role === "gm";

  // 二段階承認：現在の段（組長 or 處長）の承認者なら承認/差し戻しできる。
  const canActOnApproval =
    (contract.approval_status === "pending_team" &&
      user.role === "team_lead" &&
      user.team !== null &&
      user.team === contract.team &&
      user.department === contract.department) ||
    (contract.approval_status === "pending_div" &&
      user.role === "div_head" &&
      user.department === contract.department);

  const canEdit =
    contract.created_by === user.email &&
    (contract.approval_status === "draft" || contract.approval_status === "rejected");

  function handleError(e: unknown) {
    setError(e instanceof ApiError ? e.message : t.errors.saveFailed);
  }

  async function doApprove() {
    setBusy(true);
    setError(null);
    try {
      const res = await api.approveContract(contract.id);
      onChanged(res.contract);
    } catch (e) {
      handleError(e);
    } finally {
      setBusy(false);
    }
  }

  async function doReject() {
    if (reason.trim() === "") {
      setError(t.approval.rejectReasonRequired);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await api.rejectContract(contract.id, reason.trim());
      onChanged(res.contract);
      setRejecting(false);
      setReason("");
    } catch (e) {
      handleError(e);
    } finally {
      setBusy(false);
    }
  }

  async function onFormAction(values: ContractFormValues, action: FormAction) {
    setBusy(true);
    setError(null);
    try {
      const saved = await api.updateContract(contract.id, values);
      if (action === "resubmit") {
        const res = await api.submitContract(contract.id);
        onChanged(res.contract);
      } else {
        onChanged(saved.contract);
        setEditing(false);
      }
    } catch (e) {
      handleError(e);
    } finally {
      setBusy(false);
    }
  }

  // ---- 編集モード ----
  if (editing) {
    return (
      <Modal open title={t.form.editTitle} onClose={() => setEditing(false)}>
        <ContractForm
          mode="edit"
          initialValues={contractToFormValues(contract)}
          submitting={busy}
          serverError={error}
          onAction={onFormAction}
        />
      </Modal>
    );
  }

  // ---- 閲覧モード ----
  const footer = (
    <>
      {canEdit && (
        <Button variant="secondary" onClick={() => setEditing(true)}>
          {t.common.edit}
        </Button>
      )}
      {canActOnApproval && !rejecting && (
        <>
          <Button variant="danger" disabled={busy} onClick={() => setRejecting(true)}>
            {t.common.reject}
          </Button>
          <Button disabled={busy} onClick={doApprove}>
            {t.common.approve}
          </Button>
        </>
      )}
      {!canEdit && !canActOnApproval && (
        <Button variant="secondary" onClick={onClose}>
          {t.common.close}
        </Button>
      )}
    </>
  );

  return (
    <Modal open title={t.detail.title} onClose={onClose} footer={rejecting ? undefined : footer}>
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="text-lg font-semibold text-slate-800">{contract.contract_name}</h3>
        <div className="flex shrink-0 items-center gap-2">
          {isManagement && contract.classification && (
            <ClassificationBadge value={contract.classification} />
          )}
          <StatusBadge status={contract.approval_status} />
        </div>
      </div>

      {contract.approval_status === "rejected" && contract.rejection_reason && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3">
          <div className="text-xs font-medium text-red-700">{t.detail.rejectionReasonTitle}</div>
          <p className="mt-1 text-sm text-red-800">{contract.rejection_reason}</p>
        </div>
      )}

      <section className="mb-4">
        <h4 className="mb-1 text-sm font-semibold text-slate-600">{t.detail.factSection}</h4>
        <dl className="divide-y divide-slate-100">
          <Row label={t.fields.counterparty} value={contract.counterparty} />
          <Row label={t.fields.contractType} value={labelOf(CONTRACT_TYPES, contract.contract_type)} />
          <Row label={t.fields.continuity} value={labelOf(CONTINUITIES, contract.continuity)} />
          <Row
            label={t.fields.amount}
            value={
              <>
                {formatAmount(contract.amount, contract.currency)}
                {contract.amount_type && (
                  <span className="ml-2 text-slate-500">
                    （{labelOf(AMOUNT_TYPES, contract.amount_type)}）
                  </span>
                )}
              </>
            }
          />
          {contract.continuity !== "oneoff" && (
            <Row
              label={t.fields.period}
              value={`${formatDate(contract.period_start)} 〜 ${formatDate(contract.period_end)}`}
            />
          )}
          {contract.continuity === "auto_renew" && (
            <Row label={t.fields.nextRenewalDate} value={formatDate(contract.next_renewal_date)} />
          )}
          <Row
            label={t.fields.specialClauses}
            value={
              contract.special_clauses.length
                ? labelsOf(SPECIAL_CLAUSES, contract.special_clauses).join("、")
                : t.common.none
            }
          />
          <Row label={t.fields.department} value={contract.department} />
          <Row label={t.fields.createdBy} value={contract.created_by} />
        </dl>
      </section>

      {/* 添付ファイル（参照用副本。閲覧権限のある者が利用可。§12） */}
      <section className="mb-4">
        <h4 className="mb-1 text-sm font-semibold text-slate-600">{t.attachments.title}</h4>
        <AttachmentSection
          contractId={contract.id}
          currentEmail={user.email}
          canManage={isManagement}
        />
      </section>

      {/* 区画2: 経営側の評価・分類欄（経営側のみ。担当者には伏せる） */}
      {isManagement && (
        <section className="mb-4">
          <h4 className="mb-2 text-sm font-semibold text-slate-600">{t.detail.evaluationSection}</h4>
          <EvaluationPanel key={contract.id} contract={contract} onChanged={onChanged} />
        </section>
      )}

      {/* 区画2の例外: 本社確認(C類)の進捗は担当者にも開示（§9-2）。 */}
      {!isManagement && contract.hq_review_status && (
        <section className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-amber-800">{t.hqReview.title}</h4>
            <HqStatusBadge value={contract.hq_review_status} />
          </div>
          <p className="mt-1 text-xs text-amber-700">{t.hqReview.staffNote}</p>
        </section>
      )}

      {/* 区画3: 2系統コメントログ */}
      <section className="mb-2">
        <h4 className="mb-2 text-sm font-semibold text-slate-600">{t.detail.commentsSection}</h4>
        <CommentSection contractId={contract.id} canMeetingMemo={isManagement} />
      </section>

      {rejecting && (
        <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-4">
          <Field label={t.approval.rejectReasonLabel} required>
            <TextArea
              value={reason}
              placeholder={t.placeholders.rejectReason}
              onChange={(e) => setReason(e.target.value)}
            />
          </Field>
          {error && <p className="mt-2 text-sm text-red-700">{error}</p>}
          <div className="mt-3 flex justify-end gap-2">
            <Button variant="ghost" disabled={busy} onClick={() => setRejecting(false)}>
              {t.common.cancel}
            </Button>
            <Button variant="danger" disabled={busy} onClick={doReject}>
              {t.common.reject}
            </Button>
          </div>
        </div>
      )}

      {error && !rejecting && <p className="mt-3 text-sm text-red-700">{error}</p>}
    </Modal>
  );
}
